import { Runtime } from "fp";
import React from "react";

import * as Icon from "~/Icon";
import * as Layout from "~/Layout";
import { Order } from "~/Order";
import { Search } from "~/Search";
import { Version } from "~/Version";

export type Platform = { readonly platform: Platform.IOS | Platform.Android };

export function Platform(platform: Platform) {
  return (
    <>
      {Platform.when(platform, {
        iOS: () => <Platform.IOS />,
        android: () => <Platform.Android />,
      })}
      <Layout.Inline.Component left>
        <Version {...Platform.toVersion(platform)} />
      </Layout.Inline.Component>
    </>
  );
}

export namespace Platform {
  export const { equals, compare } = Order.fromCompare<Platform>((a, b) =>
    "android" in a.platform && "iOS" in a.platform
      ? Order.lessThan()
      : "iOS" in a.platform && "android" in a.platform
      ? Order.greaterThan()
      : Version.compare(toVersion(a), toVersion(b))
  );

  type Cases<A> = {
    readonly iOS: (iOS: IOS) => A;
    readonly android: (android: Android) => A;
  };

  export function when<A>(platform: Platform, cases: Cases<A>): A;
  export function when<A>(cases: Cases<A>): (platform: Platform) => A;
  export function when<A>(
    platformOrCases: Platform | Cases<A>,
    casesOrUndefined?: Cases<A>
  ) {
    return "platform" in platformOrCases
      ? when(casesOrUndefined as Cases<A>)(platformOrCases)
      : ({ platform }: Platform): A =>
          "iOS" in platform
            ? platformOrCases.iOS(platform)
            : platformOrCases.android(platform);
  }

  export const toString = (platform: Platform): string =>
    when(platform, { iOS: () => "iOS", android: () => "Android" });

  export const toVersion = (platform: Platform): Version =>
    when(platform, {
      iOS: ({ iOS }) => iOS,
      android: ({ android }) => android,
    });

  export const toSearch: Search.From<Platform> = (platform) =>
    Search.join(
      Search.string(Version.toString(toVersion(platform))),
      when(platform, {
        iOS: () => Search.join(Search.string("iOS"), Search.string("iPhone")),
        android: () => Search.string("Android"),
      })
    );

  export namespace Response {
    export const runtime = () =>
      Runtime.strict({
        device_information: Runtime.nullable(
          Runtime.strict({
            platform: Runtime.nullable(Runtime.string()),
            platform_version: Runtime.nullable(Runtime.string()),
          })
        ),
      });

    export const decode = (
      response?: Runtime.ToType<typeof runtime>
    ): Platform | undefined => {
      const type = response?.device_information?.platform?.toLocaleUpperCase();
      const version = response?.device_information?.platform_version
        ? Version.fromString(response?.device_information?.platform_version)
        : undefined;

      return !version
        ? undefined
        : type === "IOS"
        ? { platform: { iOS: version } }
        : type === "ANDROID"
        ? { platform: { android: version } }
        : undefined;
    };
  }

  export type IOS = { readonly iOS: Version };
  export function IOS() {
    return (
      <>
        <Layout.Inline.Component right>
          <Icon.Apple faded />
        </Layout.Inline.Component>
        iOS
      </>
    );
  }

  export type Android = { readonly android: Version };
  export function Android() {
    return (
      <>
        <Layout.Inline.Component right>
          <Icon.Android faded />
        </Layout.Inline.Component>
        Android
      </>
    );
  }
}
